import { Spinner, Text, Box } from '../../../../../../mads';
import { useI18n } from '../../../../../../shared/I18n';
import { httpGetJson } from '../../../../../native-module/httpGetJson';
import { navigateView } from '../../../../../native-module/navigateView';
import { useDomainUrl, useViewjobSourceUrl } from '../../../../context/ViewJobDataContext';
import { CompanyCustomContent } from '../CompanyCustomContent';
import { CompanyDetailJobInsight } from '../CompanyDetailJobInsight';
import { LinkButton } from '../LinkButton';

import { spacing, colors } from '@indeed/ifl-tokens/react-native';
import React, { useState, useEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';

// Matches the frontend ICompanyResponse structure
interface CompanyDetailsResponse {
    company: Record<string, string>;
    customContents: Record<string, string>[];
    companyDetails: Record<string, string>;
}

export interface CompanyContentProps {
    fccKey: string;
    mobtk: string;
}

// Company tab content component
export function CompanyContent({ fccKey, mobtk }: CompanyContentProps) {
    const domain = useDomainUrl();
    const i18n = useI18n();
    const vjSourceUrl = useViewjobSourceUrl();

    const [companyData, setCompanyData] = useState<CompanyDetailsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch company details from API (matches frontend getCompany pattern)
    useEffect(() => {
        if (!domain || !fccKey || !mobtk) {
            console.error('Missing required parameters for company fetch');
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const url = `${domain}/cmp/_r/viewjob-company?c=${encodeURIComponent(fccKey)}&tk=${encodeURIComponent(mobtk)}`;
        httpGetJson<CompanyDetailsResponse>(url)
            .then((response) => {
                if (!cancelled) {
                    setCompanyData(response);
                }
            })
            .catch((err) => {
                console.error('Error fetching company details:', err);
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [domain, fccKey, mobtk]);

    // Extract data from response (matches frontend pattern)
    const company = companyData?.company;
    const customContents = companyData?.customContents;
    const companyDetails = companyData?.companyDetails;
    const hasOffsiteUrl = Boolean(companyDetails?.offsiteUrl);

    const targetUrl = company?.url ? `${domain}/cmp/${company.url}/about` : undefined;

    const companyOffsiteClickHandler = () => {
        const offsiteUrl = companyDetails?.offsiteUrl;
        if (offsiteUrl) {
            //TODO: DISCO-3987 Add analytics tracking here if needed
        }
    };

    const shouldShowCustomContentSection = customContents && customContents.length > 0;

    const shouldShowCompanyDetailSection =
        companyDetails != null &&
        Object.values(companyDetails).some(
            (value) => value !== undefined && value !== null && value !== '',
        );

    const shouldShowCompanyLinkSection = company != null && !hasOffsiteUrl;

    const shouldShowZrpSection = !shouldShowCustomContentSection && !shouldShowCompanyDetailSection;

    return (
        <Box sx={styles.container}>
            {isLoading ? (
                <Box sx={styles.spinnerContainer}>
                    <Spinner />
                </Box>
            ) : (
                <>
                    {shouldShowCustomContentSection && (
                        <Box sx={styles.customContentsContainer}>
                            {customContents?.map((content, index) => (
                                <CompanyCustomContent key={index} {...(content as any)} />
                            ))}
                        </Box>
                    )}
                    {shouldShowCompanyDetailSection && (
                        <CompanyDetailJobInsight
                            i18n={i18n}
                            {...companyDetails}
                            companyOffsiteClickHandler={companyOffsiteClickHandler}
                        />
                    )}
                    {shouldShowZrpSection && (
                        <Box sx={styles.zrpContainer}>
                            <Text>
                                {i18n.gettext(
                                    'We are working on gathering this information for you. Visit our company page to learn more.',
                                )}
                            </Text>
                        </Box>
                    )}
                    {shouldShowCompanyLinkSection && (
                        <LinkButton
                            text={
                                company?.name
                                    ? i18n.gettext('Learn more about {0}', company.name)
                                    : i18n.gettext('Learn more about this company')
                            }
                            href={targetUrl}
                            hrefAttrs={{ target: '_blank' }}
                            onPress={() => {
                                //TODO: DISCO-3987 Add analytics tracking here if needed
                                if (Platform.OS !== 'web' && targetUrl) {
                                    navigateView(
                                        vjSourceUrl,
                                        targetUrl,
                                        targetUrl,
                                        undefined,
                                    ).catch(console.error);
                                }
                            }}
                        />
                    )}
                </>
            )}
        </Box>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    spinnerContainer: {
        alignItems: 'flex-start',
    },
    customContentsContainer: {
        marginTop: spacing.s2,
        marginBottom: spacing.s4,
    },
    testInfo: {
        marginTop: spacing.s4,
        padding: spacing.s3,
        backgroundColor: colors.backgroundWeak,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.borderWeak,
    },
    zrpContainer: {
        marginTop: spacing.s4,
        marginBottom: spacing.s4,
    },
});
